<template>
	<button class="Button" :class="classes" :type="type" :role="role" :disabled="disabled">
		<CloIcon v-if="icon" :symbol="icon" />
		<!-- @slot This slot holds the button-->
		<slot />
		<CloIcon v-if="dropdown" symbol="triangleDown" />
		<CloIcon v-if="iconAfter" :symbol="iconAfter" />
	</button>
</template>

<script>
import CloIcon from '@/icons/CloIcon/CloIcon';

export default {
	components: { CloIcon },
	name: 'CloButton',
	data() {
		return {};
	},
	props: {
		/**
		 * The type of button
		 */
		type: {
			type: String,
			default: null,
		},
		/**
		 * The role of the button
		 */
		role: {
			type: String,
			default: null,
		},
		/**
		 * Whether the button is disabled or not
		 */
		disabled: {
			type: Boolean,
			default: false,
		},
		/**
		 * include the dropdown icon
		 */
		dropdown: {
			type: Boolean,
			default: false,
		},
		/**
		 * display an icon before the label
		 */
		icon: {
			type: String,
			default: null,
		},
		/**
		 * display an icon after the label
		 */
		iconAfter: {
			type: String,
			default: null,
		},
		/**
		 * size can be xs, sm, lg, or xl
		 */
		size: {
			type: String,
			default: null,
		},
		/**
		 * reverse
		 */
		reverse: {
			type: Boolean,
			default: false,
		},
		/**
		 * hollow
		 */
		hollow: {
			type: Boolean,
			default: false,
		},
		/**
		 * color can be secondary, alert, or neutral
		 */
		color: {
			type: String,
			default: null,
		},
	},
	computed: {
		classes() {
			return {
				'Button--reverse': this.reverse,
				'Button--tiny': this.size === 'xs',
				'Button--small': this.size === 'sm',
				'Button--large': this.size === 'lg',
				'Button--huge': this.size === 'xl',
				'Button--hollow': this.hollow,
				'Button--highlight': this.color === 'secondary',
				'Button--secondary': this.color === 'neutral',
				'Button--alert': this.color === 'alert',
			};
		},
	},
};
</script>

<style lang="scss">
// Default button
.Button {
	display: inline-block;
	@include stack-md; // TODO: Remove from default?
	padding: 0.85em 1em; // TODO: Replace with standard variables?
	font-weight: bold;
	font-size: 0.9rem;
	text-align: center;
	line-height: 1;
	vertical-align: middle;
	border: 1px solid transparent;
	border-radius: $radius-sm;
	cursor: pointer;
	color: $color-white;
	background-color: $color-primary-3;
	transition: background-color 0.25s ease-out, color 0.25s ease-out;
	&:hover,
	&:focus,
	&:active {
		color: $color-white;
		background-color: darken($color-primary-3, 5%);
	}

	// Icon inside of button
	.Icon {
		@include icon-sm;
		&--large {
			@include icon-md;
			margin-right: 0.25rem; // TODO: Add logic for left vs right icon
			[dir='rtl'] & {
				margin-right: 0;
				margin-left: 0.25rem;
			}
		}
	}
}

/* --------------------------------
	Variants for size
-------------------------------- */
// TODO: Change size variant names to use --xs, --sm, --md, --lg, --xl

// Tiny
.Button--tiny {
	@include text(1);
	padding: 0.15rem 0.4rem;
	font-weight: normal;
	.Icon {
		@include icon-sm;
	}
}

// Small
.Button--small {
	@include text(2);
	@include inset-squish-sm;
	.Icon {
		@include icon-sm;
	}
	.Button-label {
		position: relative;
		top: 2px; // a little nudge for lining up text with small icons
	}
}

// Medium
.Button--medium {
	font-size: 0.85rem;
	padding: 0.7rem 0.8rem;
	.Icon {
		@include icon-sm;
	}
}

// Large
.Button--large {
	font-size: 1rem;
	.Icon {
		@include icon-sm;
	}
}

// Extra large
.Button--xlarge {
	font-size: 1.1rem;
	@include breakpoint(medium) {
		font-size: 1.2rem;
	}
}

// Huge
.Button--huge {
	font-size: 1.3rem;
	line-height: 1.3;
	padding: 1.25rem 1.5rem;
}

// Full width - TODO: Consolidate
.Button--fullWidth {
	display: block;
	width: 100%;
	margin-top: 1rem;
}

// Expanded - TODO: Consolidate
.Button--expanded {
	width: 100%;
}

// Size responsive - grows at largest screensizes
.Button--sizeResponsive {
	@include breakpoint(xlarge) {
		font-size: $text-size-md;
		.Icon {
			@include inline-xs;
			@include icon-sm;
		}
	}
	@include breakpoint(xxlarge) {
		line-height: 1.3;
		padding: 1.25rem 1.5rem;
		font-size: $text-size-lg;
		.Icon {
			@include inline-sm;
			@include icon-sm;
		}
	}
}

/* --------------------------------
	Variants for positioning
-------------------------------- */

// Icon - for buttons with icon. TODO: bake this logic into root class
.Button--icon {
	display: inline-flex;
	align-items: baseline;
	justify-content: center;
	*:not(:last-child) {
		@include inline-sm;
	}
	.Icon {
		align-self: center;
	}
}

// Left - for left aligning button label text (default is centered)
.Button--left {
	text-align: left;
	[dir='rtl'] & {
		text-align: right;
	}
	justify-content: flex-start;
}

/* --------------------------------
	Variants for color
-------------------------------- */

// Reverse - for dark backgrounds
.Button--reverse {
	background-color: $color-link-reverse;
	color: $color-primary-5;
	&:hover,
	&:focus,
	&:active {
		background-color: $color-link-hover-reverse;
		color: $color-primary-5;
	}
	&.Button--link {
		background-color: transparent;
		color: $color-link-reverse;
		&:hover,
		&:focus,
		&:active {
			background-color: transparent;
			color: $color-link-hover-reverse;
		}
	}
	&.Button--hollow {
		border-color: $color-neutral-4;
		color: $color-link-reverse;
		&:hover,
		&:focus,
		&:active {
			color: $color-link-hover-reverse;
			border-color: $color-neutral-3;
		}
	}
	&.Button--highlight {
		color: $color-white !important; // needed inside SectionHeading--reverse
	}
}

// Secondary
.Button--secondary {
	background-color: $color-neutral-3;
	color: $color-black;
	&:hover,
	&:focus,
	&:active {
		background-color: $color-neutral-4;
	}
}

// Secondary colored - TODO: Deprecate?
.Button--secondaryColored {
	color: $color-link;
}

// Highlight
.Button--highlight {
	color: $color-white;
	background-color: $color-secondary-3;
	&:hover,
	&:focus,
	&:active {
		background-color: darken($color-secondary-3, 5%);
	}
}

// Alert
.Button--alert {
	color: $color-white;
	background-color: $color-alert;
	&:hover,
	&:focus,
	&:active {
		background-color: darken($color-alert, 5%);
	}
}

// Danger
.Button--danger {
	background-color: transparent;
	color: $color-alert;
	border-color: $color-alert;
	&:hover,
	&:focus,
	&:active {
		background-color: $color-neutral-1;
		color: $color-black;
	}
}

// Hollow
.Button--hollow {
	background-color: transparent;
	color: $color-link;
	border-color: transparentize($color-link, 0.75);
	&:hover,
	&:focus,
	&:active {
		background-color: transparent;
		color: $color-link-hover;
		border-color: $color-link-hover;
	}
}

// Transparent
.Button--transparent {
	color: $color-primary;
	background-color: transparent;
	border: none;
	.Icon {
		fill: $color-primary;
	}
	&:hover,
	&:focus,
	&:active {
		background-color: transparent;
		color: $color-primary-2;
		border: none;
		.Icon {
			fill: $color-primary-2;
		}
	}
}

// Translucent
.Button--translucent {
	background-color: rgba(255, 255, 255, 0.75);
	color: $color-primary;
	border-color: $color-primary;
	&:hover,
	&:focus,
	&:active {
		background-color: rgba(255, 255, 255, 0.85);
		color: $color-primary-2;
		border-color: $color-primary-2;
	}
}

// Selected
.Button--selected {
	background-color: $color-primary-2;
	&:hover,
	&:focus,
	&:active {
		background-color: $color-primary-3; // TODO: Are we sure we want red here?
	}
}

// Disabled
.Button--disabled,
.Button:disabled {
	background-color: $color-primary-2;
	color: $color-black;
	font-weight: normal;
	&:hover,
	&:focus,
	&:active {
		background-color: $color-primary-2;
		color: $color-black;
	}
}

// Reversed
.Button--reversed {
	color: $color-black;
	border: none;
	&:hover,
	&:focus,
	&:active {
		color: $color-white;
		border: none;
	}
}

// Plain
.Button--plain {
	background-color: $color-white;
	color: $color-link;
	border: 1px solid $color-neutral-2;
	border-radius: $radius-sm;
	font-weight: normal;
	&:hover {
		background-color: $color-white;
		color: $color-link-hover;
		border-color: $color-link-hover;
	}
	&:focus,
	&:active {
		background-color: $color-white;
		color: $color-link-hover;
		border-color: $color-neutral-2;
	}
	.Icon {
		@include icon-sm;
	}
}

/* --------------------------------
	Variants for context
-------------------------------- */

// Link
.Button--link {
	display: inline;
	background-color: transparent;
	color: $color-link;
	font-weight: normal;
	white-space: nowrap;
	padding: 0;
	margin: 0;
	cursor: pointer;
	.Icon {
		vertical-align: text-bottom;
		@include icon-sm;
		&.Icon--triangleDown {
			@include icon-sm;
		}
		&.Icon--arrowRight {
			@include icon-sm;
			vertical-align: middle;
			padding-bottom: 2px;
		}
	}
	&:hover,
	&:focus,
	&:active {
		color: $color-link-hover;
		background-color: transparent;
	}
}

// Link wrappable
.Button--linkWrappable {
	line-height: 1.4;
	white-space: normal;
}

// No margin
.Button--noMargin {
	margin-bottom: 0;
}

// Tool - TODO: Consolidate with noMargin?
.Button--tool {
	margin-bottom: 0;
}

// Flush
.Button--flush {
	padding: 0.5rem;
	margin: 0;
}

// Inline - presumably for putting inline with text
.Button--inline {
	font-size: rem-calc(12);
	padding: 0.15rem 0.4rem;
	font-weight: normal;
	.Icon {
		@include icon-sm;
	}
}

// Inline only - TODO: Reconcile with --inline
.Button--inlineOnly {
	vertical-align: baseline;
	margin-left: 0.5rem;
	margin-right: 0.5rem;
}

// Map controls
.Button--mapControls {
	margin: 0;
	padding: 0.4rem;
	width: $space-lg;
	height: $space-lg;
	background-color: $color-white;
	color: $color-black;
	&:hover,
	&:focus,
	&:active {
		background-color: $color-white;
	}
	.Icon {
		@include iconSize(100%);
	}
}

// Button inside Header-menuWrapper
.Header-menuWrapper .Button {
	height: 2.3rem;
	padding-top: 0.7rem;
	margin-bottom: 0;
}

// Listen
.Button--listen {
	border-radius: 20rem;
	border: 7px solid #272726;
	background: #000;
	font-size: 1.2rem;
	transition: background-color 0.25s ease-out, color 0.25s ease-out, border-color 0.25s ease-out;
	padding: 1rem 1.5rem;
	margin-left: -7px;
	margin-bottom: 0;
	&:hover,
	&:focus {
		border-color: darken($color-alert, 10%); //F8E71C
		background-color: $color-alert;
	}
	.Icon {
		fill: $color-white;
		margin-right: 0.7rem;
		@include icon-md;
	}
}

// Listen small
.Button--listen--small {
	margin: 0;
}

// Listen light
.Button--listen--light {
	border-color: #777;
	background-color: #333;
}

// Edit revisal
.Button--editRevisal {
	background-color: $color-alert;
	&:hover,
	&:focus,
	&:active {
		background-color: lighten($color-alert, 5%);
	}
}

// Edit live
.Button--editLive {
	background-color: $color-alert;
	&:hover,
	&:focus,
	&:active {
		background-color: lighten($color-alert, 5%);
	}
}

// Underlined
.Button--underlined {
	position: relative;
	&:after {
		content: '';
		display: block;
		position: absolute;
		bottom: -1px;
		left: 0;
		[dir='rtl'] & {
			right: 0;
			left: auto;
		}
		width: 100%;
		height: 4px;
		background-color: lighten($color-link, 25%);
	}
	&.Button--reverse:after {
		background-color: $color-link-reverse;
	}
	&.Button--small:after {
		height: 2px;
	}
	&.Button--link:after {
		height: 2px;
	}
}

// Pill
.Button--pill {
	border-radius: 999px;
	margin: 0;
	/* Variants for pairing with other size variants */
	&.Button--huge {
		padding: 0.6rem 1rem;
	}
	&.Button--large {
		padding: 0.5rem 0.9rem;
	}
	&.Button--medium {
		padding: 0.4rem 0.75rem;
	}
	&.Button--small {
		padding: 0.3rem 0.6rem;
	}
	&.Button--tiny {
		padding: 0.2rem 0.4rem;
	}
}

// Active filter
.Button--activeFilter {
	display: inline-flex;
	flex-wrap: nowrap;
	align-items: center;
	border-radius: 999px;
	@include text(2);
	font-weight: normal;
	background-color: $color-overlay-white;
	color: $color-link;
	line-height: 1;
	padding: 0.3rem 0.6rem;
	margin: 0 $space-xs $space-sm 0;
	[dir='rtl'] & {
		margin: 0 0 $space-sm $space-xs;
	}
	border: 1px solid $color-neutral-2;
	border-top: 1px solid $color-white;
	border-bottom: 1px solid $color-neutral-3;
	word-break: normal;
	word-spacing: normal;
	word-wrap: normal;
	white-space: no-wrap;
	text-align: left;
	.Icon {
		@include icon-sm;
		fill: $color-black;
		margin-left: $space-xs;
		[dir='rtl'] & {
			margin-left: 0;
			margin-right: $space-xs;
		}
	}
	&:hover,
	&:active,
	&:focus {
		background-color: $color-overlay-white;
		color: $color-link-hover;
		@include dropShadow(1);
		.Icon {
			fill: $color-link-hover;
		}
	}
}

// Clear filter
.Button--clearFilter {
	font-weight: bold;
	color: $color-alert;
	.Icon {
		fill: $color-alert;
	}
	&:hover,
	&:active,
	&:focus {
		color: $color-alert;
		.Icon {
			fill: $color-alert;
		}
	}
}

// Plain text
.Button--plainText {
	color: $color-black;
	background-color: $color-white;
	text-align: left;
	[dir='rtl'] & {
		text-align: right;
	}
	border: 1px solid $color-neutral-2;
	border-radius: $radius-sm;
	font-weight: normal;
	font-size: $text-size-md;
	&:hover,
	&:focus,
	&:active {
		background-color: $color-white;
		color: $color-black;
		border-color: $color-neutral-2;
	}
}

// Faux radio
.Button--fauxRadio {
	color: $color-black;
	background-color: $color-white;
	border: 1px solid $color-neutral-2;
	border-radius: $radius-sm;
	font-weight: normal;
	font-size: $text-size-md;
	z-index: 9;
	&:hover,
	&:focus,
	&:active {
		background-color: $color-white;
		color: $color-black;
		border-color: $color-neutral-2;
		@include dropShadow(1);
	}
	&.is-checked {
		z-index: 10;
		border-color: $color-primary;
		color: $color-primary;
	}
	.Icon {
		@include icon-sm;
	}
	&.Button--reverse {
		background-color: $color-primary-5;
		color: $color-white;
		border-color: $color-neutral-4;
		&:hover,
		&:focus,
		&:active {
			background-color: darken($color-primary-5, 5%);
			color: $color-white;
			border-color: $color-neutral-4;
		}
		&.is-checked {
			color: $color-link-reverse;
			border-color: $color-neutral-4;
		}
	}
}

.Button--light {
	background-color: $color-primary-2;
	color: $color-primary-5;
	&:hover,
	&:focus,
	&:active {
		background-color: $color-primary-2;
		color: $color-primary-5;
	}
}

.Button--textDivided {
	color: $color-primary-3;
	background-color: transparent;
	text-align: left;
	@include inset-squish-sm;
	font-weight: normal;
	font-size: $text-size-md;
	border-radius: 0;
	border: 0;
	border-right: 1px solid $color-neutral-2;
	[dir='rtl'] & {
		text-align: right;
		border-right: 0;
		border-left: 1px solid $color-neutral-2;
	}
	&:last-child {
		border-right: 0;
		[dir='rtl'] & {
			border-left: 0;
		}
	}
	&:hover,
	&:focus,
	&:active {
		background-color: transparent;
		color: $color-primary-4;
	}
	&.is-checked {
		color: $color-black;
	}
}

.Button--light {
	background-color: $color-primary-2;
	color: $color-primary-5;
	&:hover,
	&:focus,
	&:active {
		background-color: $color-primary-2;
		color: $color-primary-5;
	}
}

.Button--textDivided {
	color: $color-primary-3;
	background-color: transparent;
	text-align: left;
	@include inset-squish-sm;
	font-weight: normal;
	font-size: $text-size-md;
	border-radius: 0;
	border: 0;
	border-right: 1px solid $color-neutral-2;
	&:last-child {
		border-right: 0;
	}
	&:hover,
	&:focus,
	&:active {
		background-color: transparent;
		color: $color-primary-4;
	}
	&.is-checked {
		color: $color-black;
	}
}

// button that has a triangleRight icon with 2 states
// expanded and not, and the icon animates
.Button--toggler {
	.Icon--triangleRight {
		transition: transform 0.2s ease;
		transform: rotate(0deg);
	}
	&[aria-expanded='true'] .Icon--triangleRight {
		transform: rotate(90deg);
	}
	[dir='rtl'] & {
		.Icon--triangleRight {
			transform: rotate(0deg) scaleX(-1);
		}
		&[aria-expanded='true'] .Icon--triangleRight {
			transform: rotate(-90deg) scaleX(-1);
		}
	}
}

.Button--normalWeight {
	font-weight: normal;
}
</style>
